import * as c from '../constants/app';
import { setAuthenticated, setStudentInfo } from './auth';
import { closeHelp, loadResearch } from './research';
import { loadGrades } from './grades';
import { loadSavedPreferences } from './preferences';
import { loadJournal } from './journal';
import { closeFileManager, loadConcepts } from './filemanager';
import { apiAction, initGdlAction } from '../actions/api';
import { toast, modal } from './notification';
import { setScene } from './unity';
import { syncGraphics } from './engineering';
import { loadContenders } from './simulation';
import { loadGame, loadGameHistory, loadGameRanking } from './game';
import history from '../utils/history';
import { getRoutePath } from '../config/routes';
import { announce } from './announcements';
import { getPageName } from '../utils/page-names';
import {
  PAGE_HOME,
  PAGE_RESEARCH,
  PAGE_ENGINEERING,
  PAGE_SIMULATION,
} from '../constants/pages';

export const setAppInfo = (
  code,
  name,
  contentLevel,
  specification,
  simulationAllowed,
  showQuizSummary,
  hasDistrict,
  gameMode,
  competition,
  designFields
) => ({
  type: c.APP_SET_INFO,
  code,
  name,
  contentLevel,
  specification,
  simulationAllowed,
  showQuizSummary,
  hasDistrict,
  gameMode,
  competition,
  designFields,
});

export const getAppList = () => (dispatch) => {
  dispatch(
    apiAction(
      [c.APP_GET_APPLIST, c.APP_GET_APPLIST_SUCCESS, c.APP_GET_APPLIST_FAILURE],
      (api) => api.getAppList()
    )
  );
};

export const reset = (logout = false) => ({
  type: c.APP_RESET,
  logout,
});

export const setLoading = (loading) => ({
  type: c.APP_SET_LOADING,
  loading,
});

export const init = () => (dispatch, getState) => {
  const appCode = getState().app.code;

  // don't continue if we don't have the app code.  should we forcilbly trigger a showAppList action?
  if (!appCode) {
    return;
  }
  dispatch(
    apiAction(
      [
        c.APP_INITIALIZE_START,
        c.APP_INITIALIZE_SUCCESS,
        c.APP_INITIALIZE_FAILURE,
      ],
      (api) => {
        const p = new Promise((resolve, reject) => {
          api.setConfig({ appCode });
          resolve();
        });
        return p.then(() => api.start());
      }
    )
  )
    .then((json) => {
      dispatch(setAuthenticated(true));
      dispatch(setStudentInfo(json.organization, json.activity.student.id));
      dispatch(
        setAppInfo(
          json.activity.application.code,
          json.activity.application.name,
          json.activity.contentLevel,
          json.activity.specification,
          json.activity.isSimulationAllowed,
          json.organization.showQuizSummary,
          json.organization.hasDistrict,
          json.activity.isGameModeEnabled,
          json.activity.competition,
          json.designFields
        )
      );
      dispatch(loadConcepts());
      dispatch(loadResearch(json.exercises, json.grades));
      dispatch(loadGrades(json.grades));
      dispatch(loadJournal(json.journal));
      dispatch(loadSavedPreferences());
      if (json.activity.isSimulationAllowed) {
        dispatch(loadContenders());
      }
      if (json.activity.isGameModeEnabled) {
        dispatch(loadGame(json.activity.game));
        if (json.activity.game.canChangeLevels) {
          dispatch(loadGameHistory());
          dispatch(loadGameRanking());
        }
      }
      dispatch(initGdlAction());
    })
    .catch((error) => {
      if (typeof error === 'object' && error.name === 'ApiError') {
        switch (error.responseCode) {
          case 404: // requested app does not exist
            dispatch(getAppList());
            dispatch(reset(false));
            history.push('/');
            break;
          case 403: // no permission to use this app
            dispatch(getAppList());
            dispatch(reset(false));
            history.push('/');
            
            if (error.message) {
              dispatch(
                modal('Error Loading Application', error.message, undefined, {
                  backdrop: true,
                })
              );
            }

            break;
          default:
            dispatch(toast(error.message, 'error', { sticky: true }));
            break;
        }
      } else {
        dispatch(
          modal('Error Loading Resources', error.message, undefined, {
            backdrop: true,
          })
        );
      }
    })
    .then(() => {
      dispatch(setLoading(false));
    });
};

export const setMainPage = (page) => (dispatch, getState) => {
  const {
    unity: { scene: unityScene },
    research: { exercises, selectedPage },
    filemanager: { visible: fmVisible },
    research: { isHelpOpen },
  } = getState();
  const exercise = exercises.find((e) => e.code === selectedPage);

  if (page === PAGE_ENGINEERING && unityScene !== 0) {
    dispatch(setScene(0));
    dispatch(syncGraphics());
  } else if (page === PAGE_SIMULATION && unityScene !== 1) {
    dispatch(setScene(1));
  } else if (page === PAGE_RESEARCH && exercise?.hasOwnProperty('unityScene')) {
    dispatch(setScene(exercise.unityScene));
  } else if (page === PAGE_HOME) {
    if (fmVisible) {
      dispatch(closeFileManager());
    }
    if (isHelpOpen) {
      dispatch(closeHelp());
    }
  }

  // Update Redux state
  dispatch({
    type: c.APP_SET_PAGE,
    page,
  });

  // Announce page change to screen readers
  const pageName = getPageName(page);
  dispatch(announce(`Page changed to ${pageName}`));

  // Sync URL with the new page
  const newPath = getRoutePath(page);
  if (history.location.pathname !== newPath) {
    history.push(newPath);
  }
};

export const refreshActivity = () => (dispatch, getState) => {
  dispatch(
    apiAction(
      [
        c.APP_REFRESH_ACTIVITY_START,
        c.APP_REFRESH_ACTIVITY_SUCCESS,
        c.APP_REFRESH_ACTIVITY_FAILURE,
      ],
      (api) => api.getActivity()
    )
  );
};

export const setShowTour = (show = true) => ({
  type: c.APP_SET_SHOW_TOUR,
  show,
});

export const setTourStep = (index) => ({
  type: c.APP_SET_TOUR_STEP,
  stepIndex: index,
});
